/**
 * DOM helpers for the mini map card tooltip.
 *
 * Owns all DOM creation, mutation, and event wiring for the desktop hover
 * tooltip card. The plugin (miniMapCard.plugin.js) calls these functions at
 * the appropriate lifecycle points but remains free of direct DOM operations.
 *
 * Effect boundary: every export touches the DOM. No business logic lives here.
 *
 * @module searchResultsMapVariant/components/miniMapCardDom
 */

import fastdom from 'fastdom';
import { getCardInstance, computeCardPosition } from '../services/cardBuilder';

/**
 * Creates the card overlay div and appends it to the map container.
 * The overlay sits above the map but passes pointer-events through, so only
 * the card itself (which sets pointer-events:auto) is interactive.
 *
 * @param {HTMLElement} mapContainer
 * @returns {HTMLElement} The overlay element.
 */
export function createCardOverlay(mapContainer) {
  const overlay = document.createElement('div');
  overlay.setAttribute('data-map-card-overlay', '');
  overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:visible;';
  mapContainer.appendChild(overlay);
  return overlay;
}

/**
 * Removes all card elements from the card overlay.
 *
 * @param {HTMLElement} cardOverlay
 */
export function removeCardElements(cardOverlay) {
  cardOverlay?.querySelectorAll('[data-mini-map-card]').forEach((el) => el.remove());
}

/**
 * Builds a card DOM element for the given card data.
 * Attaches one mouseleave listener using the provided callback.
 * Optionally attaches a mouseenter listener.
 *
 * @param {Object}   cardData
 * @param {Function} onMouseLeave  - Called on every mouseleave event from the card.
 * @param {Function} [onMouseEnter] - Called on every mouseenter event from the card.
 * @returns {HTMLElement|null}
 */
export function buildCardElement(cardData, onMouseLeave, onMouseEnter) {
  const cardInstance = getCardInstance(
    cardData.type,
    cardData.isBasic,
    cardData.isMasterPlannedCommunity,
  );
  if (!cardInstance) {
    return null;
  }

  cardInstance.setInformation(cardData);
  const range = document.createRange();
  const cardElement = range.createContextualFragment(cardInstance.getHtml()).firstElementChild;
  if (!cardElement) {
    return null;
  }

  cardElement.setAttribute('data-mini-map-card', '');
  cardElement.classList.add('nhs-c-tooltip--map', 'nhs-c-tooltip--card');
  cardElement.style.pointerEvents = 'auto';
  if (onMouseLeave) {
    cardElement.addEventListener('mouseleave', onMouseLeave);
  }
  if (onMouseEnter) {
    cardElement.addEventListener('mouseenter', onMouseEnter);
  }

  cardInstance.bindEvents?.(cardElement);
  return cardElement;
}

/**
 * Removes all [data-mini-map-card] elements within a map container except
 * the given card element.
 *
 * @param {HTMLElement}      mapContainer
 * @param {HTMLElement|null} [ownCard] - Card element to preserve. Omit to remove all.
 */
export function clearMiniMapCards(mapContainer, ownCard = null) {
  mapContainer?.querySelectorAll('[data-mini-map-card]').forEach((element) => {
    if (element !== ownCard) {
      element.remove();
    }
  });
}

/**
 * Sets CSS custom properties on a card element so the companion SCSS rule
 * can position it absolutely within the card overlay.
 *
 * @param {HTMLElement} card
 * @param {DOMRect}     markerRect
 * @param {DOMRect}     containerRect
 * @param {{ vertical: string, horizontal: string }|null} direction
 */
export function applyCardPixelPosition(card, markerRect, containerRect, direction) {
  const { left, right, top, bottom, transform } = computeCardPosition(markerRect, containerRect, direction);
  card.style.setProperty('--card-left',      left);
  card.style.setProperty('--card-right',     right);
  card.style.setProperty('--card-top',       top);
  card.style.setProperty('--card-bottom',    bottom);
  card.style.setProperty('--card-transform', transform);
}

/**
 * Mounts a card at a geographic position within the map container.
 *
 * Converts lat/lng coordinates to container-relative pixels using the current
 * map bounds, bypassing any dependency on a marker DOM element. Works
 * synchronously — no MutationObserver or timing workarounds needed.
 *
 * The linear interpolation is accurate enough for the zoom levels used on
 * a real-estate map; Mercator distortion within a single viewport is negligible.
 *
 * @param {HTMLElement}                               cardElement
 * @param {{ lat: number, lng: number }}              position - Geographic position of the anchor point.
 * @param {{ north: number, south: number, east: number, west: number }} bounds - Current map bounds.
 * @param {HTMLElement}                               mapContainer
 * @param {HTMLElement}                               cardOverlay
 * @param {{ vertical: string, horizontal: string }|null} direction
 */
export function mountCardAtGeoPosition(cardElement, position, bounds, mapContainer, cardOverlay, direction) {
  fastdom.measure(() => {
    const containerRect = mapContainer.getBoundingClientRect();

    const relX = (position.lng - bounds.west)  / (bounds.east  - bounds.west);
    const relY = (bounds.north - position.lat)  / (bounds.north - bounds.south);

    const pixelX = containerRect.left + relX * containerRect.width;
    const pixelY = containerRect.top  + relY * containerRect.height;

    // Synthesise a zero-size rect at the marker's geographic position so
    // computeCardPosition can apply its direction/offset logic unchanged.
    const markerRect = { left: pixelX, right: pixelX, top: pixelY, bottom: pixelY, width: 0, height: 0 };

    fastdom.mutate(() => {
      applyCardPixelPosition(cardElement, markerRect, containerRect, direction);
      cardOverlay.appendChild(cardElement);
    });
  });
}